import { ALL_BOOKINGS_OPTION, EventReportBookingOptions } from '@contexts/event-report/event-report-context.type';
import { EventAttendeesStatsResponse, EventSalesStatsAdmissions } from '@domain/event-report';
import { PassHolders } from '@domain/passes';
import { calculatePercentage } from '@utils/calculate-percentage';
import { TFunction } from 'i18next';
import moment from 'moment';

import {
  BADGES_CHECKIN_LIST,
  EVENT_REPORT_ATTENDANCE_TYPE_ATTENDANCE,
  EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME,
  EVENT_REPORT_ATTENDANCE_TYPE_RATE,
  EVENT_REPORT_BOTTLE_SERVICE_TYPE,
  EVENT_REPORT_CHECKIN_TYPE,
  EVENT_REPORT_PASS_TYPE,
  EVENT_REPORT_TAB_TIME_HOUR,
  EVENT_REPORT_TAB_TYPE_ATTENDEES,
  EVENT_REPORT_TYPES,
} from './config';
import { formatKeyLinearChart } from './linear-chart-section';
import { EventReportAreaTypes, FormatAreaChartDataArguments } from './types';
import { createDefaultAdmissionBadges, createFilteredAdmissionBadges } from '.';

interface AreaChartDataParams {
  data: EventAttendeesStatsResponse;
  bookingFilterValue: EventReportBookingOptions;
  timezone: string;
  locale: string;
  totalSoldItems?: number;
  salesStats?: EventSalesStatsAdmissions;
  totalPassHolders?: PassHolders;
}

export const totalAttendacePerType = (data: EventAttendeesStatsResponse) => {
  return EVENT_REPORT_TYPES.reduce<{ [key: string]: number }>((acc, type) => {
    acc[type] = Object.keys(data).reduce((sum, key) => {
      const dataOffers = data[key].offers;
      const currentValue = dataOffers[type]?.[EVENT_REPORT_TAB_TYPE_ATTENDEES]?.[ALL_BOOKINGS_OPTION] || 0;
      return sum + currentValue;
    }, 0);
    return acc;
  }, {});
};

const createFirstAndLastDayOfData = (data: EventAttendeesStatsResponse) => {
  const firstYearWeek = Object.keys(data)[0];
  const lastYearWeek = Object.keys(data)[Object.keys(data).length - 1];

  const firstDataDay = moment(firstYearWeek);
  const lastDataDay = moment(lastYearWeek);
  return { firstDataDay, lastDataDay };
};

export const createAdmissionsAreaData = ({ data, bookingFilterValue, locale }: AreaChartDataParams) => {
  const { firstDataDay, lastDataDay } = createFirstAndLastDayOfData(data);

  const chartData = Object.keys(data).map((key) => {
    const dataOffers = data[key].offers;
    const admissionData = EVENT_REPORT_TYPES.reduce<{ [key: string]: number }>((acc, type) => {
      acc[type] = dataOffers[type][EVENT_REPORT_TAB_TYPE_ATTENDEES][bookingFilterValue];
      return acc;
    }, {});

    return {
      date: formatKeyLinearChart(key, firstDataDay, lastDataDay, EVENT_REPORT_TAB_TIME_HOUR, locale),
      ...admissionData,
    };
  });

  return chartData;
};

export const createAdmissionsAreaDataAttendeesAccumulated = ({
  data,
  bookingFilterValue,
  locale,
}: AreaChartDataParams) => {
  const { firstDataDay, lastDataDay } = createFirstAndLastDayOfData(data);

  const accumulatedValues = EVENT_REPORT_TYPES.reduce<{ [key: string]: number }>((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {});

  const chartData = Object.keys(data).map((key) => {
    const dataOffers = data[key].offers;
    EVENT_REPORT_TYPES.forEach((type) => {
      const currentValue = dataOffers[type]?.[EVENT_REPORT_TAB_TYPE_ATTENDEES]?.[bookingFilterValue] || 0;
      accumulatedValues[type] += currentValue;
    });

    return {
      date: formatKeyLinearChart(key, firstDataDay, lastDataDay, EVENT_REPORT_TAB_TIME_HOUR, locale),
      ...accumulatedValues,
    };
  });
  return chartData;
};

export const createAdmissionsAreaDataPerSoldItemAccumulated = ({
  data,
  bookingFilterValue,
  totalSoldItems,
  salesStats,
  totalPassHolders,
  locale,
}: Required<AreaChartDataParams>) => {
  const { firstDataDay, lastDataDay } = createFirstAndLastDayOfData(data);

  const accumulatedValues = EVENT_REPORT_TYPES.reduce<{ [key: string]: number }>((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {});

  const chartData = Object.keys(data).map((key) => {
    const dataOffers = data[key].offers;

    EVENT_REPORT_TYPES.forEach((type) => {
      const currentValue = dataOffers[type]?.[EVENT_REPORT_TAB_TYPE_ATTENDEES]?.[bookingFilterValue] || 0;
      accumulatedValues[type] += currentValue;
    });

    const totalAccumulatedAttendanceAtTick = EVENT_REPORT_TYPES.reduce((sum, type) => {
      return sum + accumulatedValues[type];
    }, 0);

    const overallAttendancePercentage =
      key === EVENT_REPORT_PASS_TYPE
        ? calculatePercentage(totalAccumulatedAttendanceAtTick, totalPassHolders.active)
        : calculatePercentage(totalAccumulatedAttendanceAtTick, totalSoldItems);

    const percentageValues = EVENT_REPORT_TYPES.reduce<{ [key: string]: number }>((percentages, type) => {
      const totalGuestCapacity = salesStats[type].offers.reduce(
        (total, offer) => (offer.guestCapacity ? total + offer.guestCapacity : total),
        0
      );
      let totalSoldItemsByType = salesStats[type].soldItems[bookingFilterValue];

      if (type === EVENT_REPORT_BOTTLE_SERVICE_TYPE)
        totalSoldItemsByType = salesStats[type].soldItems[bookingFilterValue] * totalGuestCapacity;
      if (type === EVENT_REPORT_PASS_TYPE) totalSoldItemsByType = totalPassHolders.active;
      if (type === EVENT_REPORT_CHECKIN_TYPE) totalSoldItemsByType = accumulatedValues[type];

      percentages[type] = calculatePercentage(accumulatedValues[type], totalSoldItemsByType);
      return percentages;
    }, {});

    return {
      date: formatKeyLinearChart(key, firstDataDay, lastDataDay, EVENT_REPORT_TAB_TIME_HOUR, locale),
      ...percentageValues,
      totalAttendanceRate: overallAttendancePercentage,
    };
  });

  return chartData;
};

export const createDataTypeTabs = (t: TFunction<'translation', undefined>) => {
  return [
    {
      id: EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME,
      name: t('bookingDetail:entryTimeLabel'),
    },
    {
      id: EVENT_REPORT_ATTENDANCE_TYPE_ATTENDANCE,
      name: t('eventReport:attendeesLabel'),
    },
    {
      id: EVENT_REPORT_ATTENDANCE_TYPE_RATE,
      name: t('eventReport:attendeesRateTitle'),
    },
  ];
};

const dataByType = (type: EventReportAreaTypes) => {
  switch (type) {
    case EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME:
      return createAdmissionsAreaData;
    case EVENT_REPORT_ATTENDANCE_TYPE_RATE:
      return createAdmissionsAreaDataPerSoldItemAccumulated;
    default:
      return createAdmissionsAreaDataAttendeesAccumulated;
  }
};

export const formatDataAreaChartSection = ({
  data,
  eventSales,
  t,
  bookingFilterValue = ALL_BOOKINGS_OPTION,
  dataType = EVENT_REPORT_ATTENDANCE_TYPE_ENTRY_TIME,
  salesStats,
  timezone,
  locale,
}: FormatAreaChartDataArguments & { timezone: string; locale: string }) => {
  const defaultInfo = {
    defaultDataTypeTab: dataType,
    dataTypeTabs: createDataTypeTabs(t),
  };

  if (!data)
    return {
      data: [],
      event: undefined,
      categories: createDefaultAdmissionBadges(t),
      ...defaultInfo,
    };

  const formattedData = dataByType(dataType)({
    data,
    bookingFilterValue,
    totalSoldItems: eventSales.event.soldItems[bookingFilterValue],
    salesStats,
    totalPassHolders: eventSales.season_pass.passHolders,
    timezone,
    locale,
  });

  const defaultCategories = createFilteredAdmissionBadges(t, eventSales, bookingFilterValue, BADGES_CHECKIN_LIST);
  const categories =
    dataType === EVENT_REPORT_ATTENDANCE_TYPE_RATE
      ? [
          ...defaultCategories,
          {
            id: 'totalAttendanceRate',
            label: t('eventReport:totalAttendanceRate'),
            color: '#000000',
            isActive: false,
          },
        ]
      : defaultCategories;
  const totalAttendancePerType = totalAttendacePerType(data);

  return {
    data: formattedData,
    event: eventSales.event,
    categories,
    totalAttendancePerType,
    ...defaultInfo,
  };
};
